import React, { useMemo, useState } from 'react';

import { axisScale, curve, endLabelMargin, referenceMarks, scaleBounds, valueLabels, xAxisName, xLabelSpacing, xTitleRoom, yAxisName } from '../axisStyle';
import { formatAxis, formatValue, memberLabel, monthOf, pointLabel, shortDay, unitName } from '../format';
import type { Palette } from '../palette';
import { overrideColor, seriesOverrides } from '../seriesStyle';
import { timeAxis } from '../timeAxis';
import type { Block, Envelope, EnvelopeColumn, Grain, WindowOut } from '../types';

import Chart, { type ChartOption } from './Chart';
import type { LegendItem } from './Legend';

type TrendProps = {
  block: Block;
  kind: 'line' | 'area' | 'column';
  envelope: Envelope;
  lensWindow: WindowOut;
  palette: Palette;
  width: number;
  axisRange?: ZoomRange;
};

type ZoomRange = { from: string; to: string };

const LABEL_SHARE = 0.25;
const SHORT_SPAN_DAYS = 62;
const DAY_TICKS = 5;
const CHAR_WIDTH = 6.4;

const SERIES_NAMES: Record<string, string> = { ly: 'Last year', l2y: 'Two years ago', prior: 'Prior period' };
const MIN_ZOOM_POINTS = 3;
const ZOOM_CHIP_ROOM = 30;
const MONTHS_IN: Partial<Record<Grain, number>> = { month: 1, quarter: 3, year: 12 };

function peakOf(values: Array<number | null>, partial: Array<boolean>): number {
  let peak = -1;
  values.forEach((value, index) => {
    if (value !== null && !partial[index] && (peak < 0 || value > (values[peak] as number))) peak = index;
  });
  return peak;
}

const instantOf = (day: string) => Date.parse(day.includes('T') ? `${day}:00Z` : `${day}T00:00:00Z`);

function stepOf(days: Array<string>, fallback: Grain | undefined): Grain | undefined {
  if (days[0]?.includes('T')) return fallback === 'minute' || days.some((day) => day.slice(14, 16) !== '00') ? 'minute' : 'hour';
  if (days.length < 2) return fallback;
  const gap = Math.min(...days.slice(1).map((day, index) => (Date.parse(`${day}T00:00:00Z`) - Date.parse(`${days[index]}T00:00:00Z`)) / 86_400_000));
  return gap <= 1 ? 'day' : gap <= 7 ? 'week' : gap <= 31 ? 'month' : gap <= 92 ? 'quarter' : 'year';
}

const STEP_MONTHS: Partial<Record<Grain, number>> = { month: 1, quarter: 3, year: 12 };

function filledDays(days: Array<string>, fallback: Grain | undefined): Array<string> {
  const grain = stepOf(days, fallback);
  const months = grain && STEP_MONTHS[grain];
  if (days.length < 2 || !months || !/^\d{4}-\d{2}-01$/.test(days[0])) return days;
  const filled: Array<string> = [];
  for (let at = new Date(`${days[0]}T00:00:00Z`); at.toISOString().slice(0, 10) <= days[days.length - 1]; at.setUTCMonth(at.getUTCMonth() + months)) filled.push(at.toISOString().slice(0, 10));
  return filled;
}

function trendDays(envelope: Envelope, block: Block): Array<string> {
  const measure = envelope.columns.find((column) => column.role === 'measure');
  const all = [...new Set(envelope.rows.map((row) => String(row.t)))].sort();
  const valued = new Set(envelope.rows.filter((row) => measure && row[measure.name] !== null && row[measure.name] !== undefined).map((row) => String(row.t)));
  const first = all.findIndex((day) => valued.has(day));
  const last = all.length - 1 - [...all].reverse().findIndex((day) => valued.has(day));
  const days = filledDays(first < 0 ? all : all.slice(first, last + 1), block.grain ?? envelope.period.grain);
  const grain = stepOf(days, block.grain ?? envelope.period.grain);
  const additive = measure?.additive !== false && measure?.format !== 'pct';
  const clipped = days.map((day) => additive && isClipped(bucketSpan(day, grain, envelope)) && !envelope.rows.some((row) => row.t === day && row.partial));
  return clipped.every(Boolean) ? days : days.filter((_, at) => !clipped[at]);
}

type BucketSpan = { from: string; to: string; days: number; full: number };

const dayCount = (from: string, to: string) => Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) + 1;
const isClipped = (span: BucketSpan) => span.full > 1 && span.days < span.full;

function bucketSpan(day: string, grain: Grain | undefined, envelope: Envelope): BucketSpan {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return { from: day, to: day, days: 1, full: 1 };
  const end = lastDayOf(day, grain);
  const through = [end, envelope.period.current.to, envelope.completeThrough].filter((entry): entry is string => Boolean(entry)).sort()[0];
  const from = day < envelope.period.current.from ? envelope.period.current.from : day;
  return { from, to: through, days: Math.max(0, dayCount(from, through)), full: dayCount(day, end) };
}

function spanText(span: BucketSpan): string {
  const range = span.from.slice(0, 7) === span.to.slice(0, 7) ? `${Number(span.from.slice(8))}–${shortDay(span.to)}` : `${shortDay(span.from)} – ${shortDay(span.to)}`;
  return `${range}, ${span.days} ${span.days === 1 ? 'day' : 'days'}`;
}

const END_LABEL_GAP = 16;

function endLabelOffsets(lines: Array<{ name: string; values: Array<number | null> }>, plotHeight: number): Map<string, number> {
  const all = lines.flatMap((line) => line.values.filter((value): value is number => value !== null));
  const top = Math.max(...all);
  const range = top - Math.min(...all) || 1;
  const ends = lines
    .map((line) => ({ name: line.name, last: [...line.values].reverse().find((value): value is number => value !== null) }))
    .filter((end): end is { name: string; last: number } => end.last !== undefined)
    .map((end) => ({ name: end.name, y: ((top - end.last) / range) * plotHeight }))
    .sort((a, b) => a.y - b.y);
  const offsets = new Map<string, number>();
  let previous = -Infinity;
  for (const end of ends) {
    const placed = Math.max(end.y, previous + END_LABEL_GAP);
    offsets.set(end.name, placed - end.y);
    previous = placed;
  }
  return offsets;
}

const escaped = (text: string) => text.replace(/[&<>"]/g, (character) => `&#${character.charCodeAt(0)};`);

function alignInside(at: number, count: number, text: string, plotWidth: number): 'left' | 'center' | 'right' {
  const x = count > 1 ? (at / (count - 1)) * plotWidth : plotWidth / 2;
  const half = (text.length * CHAR_WIDTH) / 2;
  return x < half ? 'left' : plotWidth - x < half ? 'right' : 'center';
}

function trendLines({ block, kind, envelope, palette, width, axisRange }: Omit<TrendProps, 'lensWindow'>) {
  const measure = envelope.columns.find((column) => column.role === 'measure') as EnvelopeColumn;
  const split = envelope.columns.find((column) => column.role === 'dim');
  const days = timeAxis(trendDays(envelope, block), block, envelope, axisRange).days;
  const partial = days.map((day) => envelope.rows.some((row) => row.t === day && row.partial));
  const valuesOf = (rows: Envelope['rows'], key?: string) => {
    const byDay = new Map(rows.filter((row) => !split || row[split.name] === key).map((row) => [String(row.t), row[measure.name] as number | null]));
    return days.map((day) => byDay.get(day) ?? null);
  };
  const totals = new Map<string, number>();
  const others = new Set<string>();
  if (split)
    for (const row of envelope.rows) {
      const key = String(row[split.name]);
      totals.set(key, (totals.get(key) ?? 0) + Math.abs(Number(row[measure.name]) || 0));
      if (row.other) others.add(key);
    }
  const keys = split ? [...totals.keys()].sort((left, right) => Number(others.has(left)) - Number(others.has(right)) || (totals.get(right) ?? 0) - (totals.get(left) ?? 0)) : [undefined];
  const overrides = seriesOverrides(block);
  const current = keys.map((key, index) => ({
    key: key ?? measure.name,
    name: overrides[key ?? measure.name]?.label ?? (key === undefined ? (Object.keys(envelope.compare).some((id) => id === 'ly' || id === 'l2y') ? 'This year' : 'This period') : memberLabel(key)),
    values: valuesOf(envelope.rows, key),
    color: overrideColor(overrides[key ?? measure.name], palette, key !== undefined && others.has(key) ? palette.muted : palette.series[index % palette.series.length]),
    compare: false,
  })).filter((entry) => !overrides[entry.key]?.hidden);
  const compared = split
    ? []
    : Object.entries(envelope.compare)
        .filter(([id]) => !overrides[id]?.hidden)
        .map(([id, rows]) => ({ key: id, name: overrides[id]?.label ?? SERIES_NAMES[id] ?? id, values: valuesOf(rows), color: overrideColor(overrides[id], palette, palette.muted), compare: true }));
  const all = [...current, ...compared];
  const endWidth = Math.max(...all.map((series) => series.name.length)) * 6.4 + 14;
  const labels = block.labels ?? (all.length === 1 && !compared.length ? 'none' : all.length > 1 && (all.length > 5 || endWidth > (width || 1200) * LABEL_SHARE) ? 'legend' : 'end');
  const direct = labels === 'end' && kind !== 'column';
  const legend = all.length > 1 && (labels === 'legend' || (labels === 'end' && kind === 'column'));
  const labelWidth = direct ? Math.min(endWidth, (width || 1200) * LABEL_SHARE) : 12;
  return { measure, days, partial, current, compared, all, direct, legend, labelWidth };
}

export function trendLegend(props: Omit<TrendProps, 'lensWindow'>): Array<LegendItem> | undefined {
  const { all, legend } = trendLines(props);
  return legend ? all.map((entry) => ({ name: entry.name, color: entry.color, mark: entry.compare ? 'dash' : props.kind === 'column' ? 'square' : 'line', part: `series:${entry.key}` })) : undefined;
}

export function trendOption({ block, kind, envelope, lensWindow, palette, width, axisRange }: TrendProps, headroom = 0): ChartOption {
  const { measure, days, partial, current, all, direct, labelWidth } = trendLines({ block, kind, envelope, palette, width, axisRange });
  const right = direct ? Math.max(12, ...all.map((entry) => endLabelMargin(entry.values, labelWidth, width || 1200))) : 12;
  const start = days.findIndex((day) => day >= lensWindow.from);
  const end = days.reduce((found, day, index) => (day.slice(0, 10) <= lensWindow.to ? index : found), -1);
  const shaded = (start > 0 || (end >= 0 && end < days.length - 1)) && (lensWindow.from !== envelope.period.current.from || lensWindow.to !== envelope.period.current.to);
  const mark = block.mark ?? (current.length === 1 ? 'peak' : 'none');
  const marked = !current.length ? -1 : mark === 'peak' ? peakOf(current[0].values, partial) : mark === 'latest' ? current[0].values.reduce<number>((last, value, index) => (value !== null ? index : last), -1) : -1;
  const bars = kind === 'column';
  const spanDays = days.length > 1 ? (instantOf(days[days.length - 1]) - instantOf(days[0])) / 86_400_000 : 0;
  const step = spanDays <= SHORT_SPAN_DAYS ? Math.max(1, Math.ceil(days.length / DAY_TICKS)) : 0;
  const grain = stepOf(days, block.grain ?? envelope.period.grain);
  const time = timeAxis(days, block, envelope, axisRange);
  const markText = marked >= 0 ? `${pointLabel(days[marked], grain)} · ${formatValue(current[0].values[marked], measure.format)}` : '';

  const scale = axisScale(block);
  const labelOffsets = endLabelOffsets(all, block.size === 'compact' ? 160 : block.size === 'tall' ? 420 : 260);
  const series = all.flatMap((entry, index) => {
    const tailed = !entry.compare && partial.some(Boolean);
    const solid = tailed ? entry.values.map((value, at) => (partial[at] ? null : value)) : entry.values;
    const barred = bars && !entry.compare;
    const onlySoFar = tailed && solid.every((value) => value === null);
    const soFarLabel = { show: true, position: 'top', distance: 6, formatter: ({ value }: { value: number }) => `${formatValue(value, measure.format)} so far`, color: palette.ink2, fontSize: 11, fontFamily: palette.sans };
    const barData = entry.values.map((value, at) => (partial[at] ? { value, itemStyle: { color: entry.color, opacity: 0.4 }, ...(onlySoFar ? { label: soFarLabel } : {}) } : value));
    const endLabel = { show: direct, width: labelWidth - 10, overflow: 'truncate', formatter: () => entry.name, color: entry.compare ? palette.ink3 : entry.color, fontSize: 11, fontFamily: palette.sans, distance: 6, offset: [0, labelOffsets.get(entry.name) ?? 0] };
    const main = {
      id: entry.key,
      name: entry.name,
      type: barred ? 'bar' : 'line',
      data: barred ? barData : solid,
      showSymbol: false,
      z: entry.compare ? 1 : 3,
      barMaxWidth: 18,
      itemStyle: { color: entry.color },
      lineStyle: { width: entry.compare ? 1.25 : 1.75, type: entry.compare ? [4, 3] : 'solid', color: entry.color },
      ...(kind === 'area' && !entry.compare ? { areaStyle: { color: entry.color, opacity: 0.08 } } : {}),
      endLabel: { ...endLabel, show: direct && !tailed },
      labelLayout: { moveOverlap: 'shiftY' },
      ...(entry.compare ? {} : valueLabels(block, entry.values, measure.format, palette)),
      ...(barred ? {} : curve(block)),
      ...(index === 0 && shaded
        ? {
            markArea: {
              silent: true,
              itemStyle: { color: palette.shade },
              label: { show: true, position: 'insideTop', formatter: lensWindow.label, color: palette.ink3, fontSize: 10.5, fontFamily: palette.sans },
              data: [[{ xAxis: Math.max(0, start) }, { xAxis: end < 0 ? days.length - 1 : end }]],
            },
          }
        : {}),
      ...(index === 0 ? referenceMarks(block, palette, measure.format) : {}),
      ...(index === 0 && marked >= 0
        ? {
            markPoint: {
              symbol: 'circle',
              symbolSize: bars ? 0 : 6,
              itemStyle: { color: entry.color },
              data: [{ coord: [marked, entry.values[marked]] }],
              label: {
                show: true,
                position: 'top',
                distance: 6,
                align: alignInside(marked, days.length, markText, (width || 1200) - right),
                formatter: markText,
                color: palette.ink2,
                fontSize: 11,
                fontFamily: palette.sans,
              },
            },
          }
        : {}),
    };
    const lone = main.type === 'line' ? solid.map((value, at) => (value !== null && solid[at - 1] == null && solid[at + 1] == null ? value : null)) : [];
    const dots = lone.some((value) => value !== null) ? [{ name: entry.name, type: 'scatter', data: lone, symbolSize: entry.compare ? 4 : 5, z: main.z, itemStyle: { color: entry.color } }] : [];
    if (!tailed || barred) return [main, ...dots];
    const tail = entry.values.map((value, at) => (partial[at] || partial[at + 1] ? value : null));
    const tailEnd = tail.reduce<number>((last, value, at) => (value !== null ? at : last), -1);
    const tailData = tail.map((value, at) => (at === tailEnd && (current.length === 1 || onlySoFar) ? { value, label: soFarLabel } : value));
    return [main, ...dots, { id: `${entry.key}:tail`, name: `${entry.name} so far`, type: 'line', data: tailData, showSymbol: true, symbolSize: onlySoFar ? 9 : 6, lineStyle: { width: 1.5, type: [2, 3], color: entry.color }, itemStyle: { color: palette.surface, borderColor: entry.color, borderWidth: onlySoFar ? 2 : 1.5 }, endLabel }];
  });
  const tailColors = new Map(current.map((entry) => [`${entry.key}:tail`, entry.color]));

  return {
    animation: false,
    textStyle: { fontFamily: palette.sans, fontSize: 11, color: palette.ink3 },
    grid: { left: 4, right, top: (shaded ? 22 : 16) + headroom, bottom: 4 + xTitleRoom(block), containLabel: true },
    tooltip: {
      trigger: 'axis',
      backgroundColor: palette.surface,
      borderColor: palette.hairlineStrong,
      textStyle: { color: palette.ink, fontSize: 12, fontFamily: palette.sans },
      confine: true,
      formatter: (items: Array<{ axisValue: string; dataIndex: number; marker: string; seriesId?: string; seriesName: string; value: number | { value: number } | null }>) => {
        const shownItems = items.filter((item) => pointValue(item.value) !== null && item.value !== undefined && (!tailColors.has(item.seriesId ?? '') || partial[item.dataIndex]));
        const span = bucketSpan(items[0]?.axisValue ?? '', grain, envelope);
        const header = `<div style="margin-bottom:4px;color:${palette.ink3}">${isClipped(span) ? spanText(span) : pointLabel(items[0]?.axisValue ?? '', grain, true)}</div>`;
        const markerOf = (item: (typeof items)[number]) => (tailColors.has(item.seriesId ?? '') ? `<span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${tailColors.get(item.seriesId!)};"></span>` : item.marker);
        return header + shownItems.map((item) => `<div>${markerOf(item)}${escaped(item.seriesName)} <b style="float:right;margin-left:16px">${formatValue(pointValue(item.value) ?? 0, measure.format)}</b></div>`).join('');
      },
    },
    xAxis: {
      type: 'category',
      triggerEvent: true,
      data: days,
      boundaryGap: bars,
      ...xAxisName(block, palette),
      axisLine: { lineStyle: { color: palette.hairlineStrong } },
      axisTick: { show: false },
      axisLabel: {
        color: palette.ink3,
        fontSize: 11,
        hideOverlap: true,
        interval: ((index: number, day: string) => (step ? index % step === 0 : index === 0 ? day.endsWith('-01') : day.slice(5, 7) !== days[index - 1]?.slice(5, 7))),
        formatter: time.label ?? ((day: string) =>
          grain === 'month' ? monthOf(day) : grain === 'quarter' || grain === 'year' ? pointLabel(day, grain) : step ? shortDay(day) : monthOf(day)),
        ...xLabelSpacing(block), ...(time.tick ? { interval: time.tick } : {}),
      },
    },
    yAxis: {
      type: 'value',
      scale: !bars && !scale.zero,
      triggerEvent: true,
      ...scaleBounds(scale),
      splitNumber: 4,
      ...yAxisName(block, unitName(measure.format, palette.ink3), palette),
      axisLabel: { color: palette.ink3, fontSize: 11, formatter: (value: number) => formatAxis(value, measure.format) },
      splitLine: { lineStyle: { color: palette.hairline } },
    },
    series,
  };
}

function withinRange(envelope: Envelope, { from, to }: ZoomRange): Envelope {
  const inside = (row: Envelope['rows'][number]) => String(row.t) >= from && String(row.t) <= to;
  return { ...envelope, rows: envelope.rows.filter(inside), compare: Object.fromEntries(Object.entries(envelope.compare).map(([id, rows]) => [id, rows.filter(inside)])) };
}

function lastDayOf(day: string, grain: Grain | undefined): string {
  const start = new Date(`${day}T00:00:00Z`);
  if (grain === 'week') start.setUTCDate(start.getUTCDate() + 6);
  else if (grain && MONTHS_IN[grain]) start.setUTCMonth(start.getUTCMonth() + MONTHS_IN[grain]!, 0);
  return start.toISOString().slice(0, 10);
}

const pointValue = (value: number | { value: number } | null): number | null => (typeof value === 'number' ? value : value?.value ?? null);

export default function Trend({ block, kind, envelope, lensWindow, palette, width, onPeriod }: TrendProps & { onPeriod?: (period: string) => void }) {
  const [zoom, setZoom] = useState<ZoomRange>();
  const days = useMemo(() => timeAxis(trendDays(envelope, block), block, envelope).days, [envelope, block]);
  const zoomed = zoom && days.includes(zoom.from) && days.includes(zoom.to) ? zoom : undefined;
  const shown = useMemo(() => (zoomed ? withinRange(envelope, zoomed) : envelope), [envelope, zoomed?.from, zoomed?.to]);
  const option = useMemo(
    () => trendOption({ block, kind, envelope: shown, lensWindow, palette, width, axisRange: zoomed }, zoomed ? ZOOM_CHIP_ROOM : 0),
    [block, kind, shown, lensWindow, palette, width, zoomed],
  );
  const rangeOf = (range: ZoomRange | undefined) => (range && days.includes(range.from) && days.includes(range.to) ? [days.indexOf(range.from), days.indexOf(range.to)] : [0, days.length - 1]);
  const zoomTo = (from: number, to: number) => setZoom(to - from + 1 >= days.length ? undefined : { from: days[from], to: days[to] });
  const chartZoom = {
    pick: (first: number, last: number) => {
      const [from] = rangeOf(zoomed);
      if (last - first + 1 >= Math.min(MIN_ZOOM_POINTS, days.length)) zoomTo(from + first, from + last);
    },
    scale: (count: number, anchor: number) =>
      setZoom((current) => {
        const [from, to] = rangeOf(current);
        const next = Math.max(Math.min(MIN_ZOOM_POINTS, days.length), Math.min(days.length, count));
        if (next >= days.length) return undefined;
        const fixed = from + anchor * (to - from);
        const start = Math.round(Math.max(0, Math.min(days.length - next, fixed - anchor * (next - 1))));
        return { from: days[start], to: days[start + next - 1] };
      }),
    reset: () => setZoom(undefined),
  };
  const daily = zoomed && /^\d{4}-\d{2}-\d{2}$/.test(zoomed.from);
  const overlay = zoomed ? (
    <div className="zoom-chips">
      <button className="chip" onClick={() => setZoom(undefined)} type="button">
        Reset zoom
      </button>
      {onPeriod && daily ? (
        <button
          className="chip"
          onClick={() => {
            onPeriod(`${zoomed.from}..${lastDayOf(zoomed.to, stepOf(days, block.grain))}`);
            setZoom(undefined);
          }}
          type="button"
        >
          Use this range for the whole Lens
        </button>
      ) : null}
    </div>
  ) : null;
  return <Chart option={option} overlay={overlay} zoom={days.length > MIN_ZOOM_POINTS ? chartZoom : undefined} />;
}
